import {
  IonContent,
  IonIcon,
  IonPage,
  IonToggle,
  IonLabel,
  IonSpinner,
} from '@ionic/react';
import './MapPage.css';
import { observer } from 'mobx-react-lite';
import MapComponent, { MapMarker } from 'components/MapComponent';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useStores } from '../../models';
import { useExploreElementStore } from '../../models/ExploreElementStore';
import { RunModel } from '../../models/RunModel/RunModel';
import { useHistory } from 'react-router';
import { Trans } from 'react-i18next';
import Icon from '../../components/icons/Icon';
import { ExploreElementType } from '../../spinnaxBase/data/explore';
import { logger } from 'services/Logger';
import { RankerService } from 'services/freak_ranker';

interface SkateboardSpot {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  type: string;
  rating?: number;
  description?: string;
}

const MapPage = observer(() => {
  const { recordList } = useStores();
  const exploreStore = useExploreElementStore();
  const history = useHistory();
  const spots = useMemo(() => [] as SkateboardSpot[], []);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [ionSecondaryColor, setIonSecondaryColor] = useState('#3dc2ff');
  const [showOnlyOwnRuns, setShowOnlyOwnRuns] = useState(false);
  const [publicLocations, setPublicLocations] = useState<
    Array<{ id: string; location: string; latitude: number; longitude: number }>
  >([]);

  // Get the actual Ionic secondary color from CSS variables
  useEffect(() => {
    const getIonicSecondaryColor = () => {
      const computedStyle = getComputedStyle(document.documentElement);
      const secondaryColor = computedStyle
        .getPropertyValue('--ion-color-secondary')
        .trim();

      if (secondaryColor) {
        if (secondaryColor.startsWith('#')) {
          return secondaryColor;
        }
        if (secondaryColor.includes(',')) {
          return `rgb(${secondaryColor})`;
        }
        return `rgb(${secondaryColor})`;
      }

      return '#3dc2ff';
    };

    const color = getIonicSecondaryColor();
    setIonSecondaryColor(color);
  }, []);

  // Icon-Name basierend auf Spot-Typ
  const getSpotIcon = (type: string): string => {
    switch (type) {
      case 'park':
        return 'ParkMapIcon';
      case 'street':
        return 'CityIcon';
      case 'bowl':
        return 'CityIcon';
      case 'trick':
        return 'HotSpotMapIcon';
      case 'event':
        return 'EventMapIcon';
      default:
        return 'PlayMapIcon';
    }
  };

  // Handle Run Location Click
  const handleRunClick = useCallback(
    (trick: RunModel) => {
      history.push(`/trick/${trick.id}`);
    },
    [history]
  );

  // Load spots and user location
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await exploreStore.loadElements();
        // Public Run Locations laden
        const locationsResult = await new RankerService().getAllRunLocations();
        // API liefert vermutlich { locations: [...] }
        let locations: Array<{
          id: string;
          location: string;
          latitude: number;
          longitude: number;
        }> = [];
        if (Array.isArray(locationsResult)) {
          // Fallback: falls API direkt ein Array liefert
          locations = locationsResult
            .filter((l: any) => l.latitude && l.longitude)
            .map((l: any, idx: number) => ({
              id: l.id || `${l.latitude},${l.longitude},${idx}`,
              location: l.location || '',
              latitude: l.latitude,
              longitude: l.longitude,
            }));
        } else if (
          locationsResult &&
          Array.isArray((locationsResult as any).locations)
        ) {
          locations = (locationsResult as any).locations
            .filter((l: any) => l.latitude && l.longitude)
            .map((l: any, idx: number) => ({
              id: l.id || `${l.latitude},${l.longitude},${idx}`,
              location: l.location || '',
              latitude: l.latitude,
              longitude: l.longitude,
            }));
        }
        setPublicLocations(locations);
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setUserLocation({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });
            },
            (error) => {
              logger.error('Error getting location:', error);
            }
          );
        }
        setLoading(false);
      } catch (error) {
        logger.error('Error loading map data:', error);
        setLoading(false);
      }
    };
    loadData();
  }, [exploreStore]);

  // Marker für Events generieren - ALLE verwenden Ionic Secondary Color
  const eventMarkers: MapMarker[] = useMemo(() => {
    const events = exploreStore.elements.filter(
      (element) =>
        element.type === ExploreElementType.Event &&
        element.latitude !== null &&
        element.longitude !== null
    );
    return events.map((event) => ({
      id: event.id,
      lat: event.latitude!,
      long: event.longitude!,
      title: event.name,
      iconName: getSpotIcon('event'),
      color: ionSecondaryColor, // Force Ionic secondary color
      width: 40,
      onClick: () => {
        history.push('/explore', { selectedEvent: event });
      },
    }));
  }, [exploreStore.elements, history, ionSecondaryColor]);

  // Marker aus RunModels erstellen - je nach Toggle
  const runMarkers: MapMarker[] = useMemo(() => {
    const runs = showOnlyOwnRuns ? recordList.tricks : recordList.publicRuns;
    return runs
      .filter((trick) => {
        const hasLocation =
          trick.recordData?.location || trick.playData?.location;
        return (
          hasLocation &&
          hasLocation.latitude !== undefined &&
          hasLocation.longitude !== undefined
        );
      })
      .map((trick, index) => {
        const location = trick.recordData?.location || trick.playData?.location;
        const title =
          trick.recordData?.title || trick.playData?.title || 'Trick';
        return {
          id: `trick-${trick.recordId}-${index}`,
          lat: location!.latitude!,
          long: location!.longitude!,
          title: title,
          type: 'trick' as const,
          width: 35,
          onClick: () => handleRunClick(trick),
        };
      });
  }, [
    showOnlyOwnRuns,
    recordList.tricks,
    recordList.publicRuns,
    handleRunClick,
  ]);

  // Marker für Public Run Locations (dunkle, nicht-interaktive Punkte)
  const publicLocationMarkers: MapMarker[] = useMemo(() => {
    // Sekundärfarbe mit 50% Helligkeit (Alpha)
    const secondaryColor = ionSecondaryColor.startsWith('#')
      ? ionSecondaryColor + '80' // #RRGGBB80 für 50% alpha
      : 'rgba(61, 194, 255, 0.5)'; // Fallback für rgb()
    // Nur anzeigen, wenn NICHT showOnlyOwnRuns aktiv ist (also Public-Ansicht)
    if (showOnlyOwnRuns) return [];
    return publicLocations.map((loc) => ({
      id: `publicloc-${loc.id}`,
      lat: loc.latitude,
      long: loc.longitude,
      title: typeof loc.location === 'string' ? loc.location : undefined,
      markerType: 'publicLocation',
      color: secondaryColor,
      onClick: undefined, // Interaktivität nur über MapComponent Toast
    }));
  }, [publicLocations, ionSecondaryColor, showOnlyOwnRuns]);

  // Alle Marker kombinieren - ALLE verwenden Ionic Secondary Color
  const allMarkers = useMemo(() => {
    const spotMarkers = spots.map((spot) => ({
      id: spot.id,
      lat: spot.latitude,
      long: spot.longitude,
      title: spot.name,
      iconName: getSpotIcon(spot.type),
      color: ionSecondaryColor, // Force Ionic secondary color
      onClick: () => {
        // Handle spot click
      },
    }));

    return [
      ...spotMarkers,
      ...eventMarkers,
      ...runMarkers,
      ...publicLocationMarkers,
    ];
  }, [
    spots,
    eventMarkers,
    runMarkers,
    publicLocationMarkers,
    ionSecondaryColor,
  ]);

  // Standard-Position berechnen
  const getMapCenter = (): { lat: number; lng: number } => {
    if (userLocation) {
      return { lat: userLocation.lat, lng: userLocation.lng };
    }

    // Für beide Modi (Public/Private) nur runMarkers für die Berechnung verwenden
    if (runMarkers.length > 0) {
      const avgLat =
        runMarkers.reduce((sum, marker) => sum + marker.lat, 0) /
        runMarkers.length;
      const avgLng =
        runMarkers.reduce((sum, marker) => sum + marker.long, 0) /
        runMarkers.length;
      return { lat: avgLat, lng: avgLng };
    }

    return { lat: 52.52, lng: 13.405 };
  };

  const mapCenter = getMapCenter();

  if (loading) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <IonSpinner name="crescent" style={{ width: 48, height: 48 }} />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage id="map-page" style={{ height: '100vh', width: '100vw' }}>
      <IonContent
        forceOverscroll={false}
        fullscreen
        style={{
          height: '100vh',
          width: '100vw',
          '--padding-top': '0px',
          '--padding-bottom': '0px',
          '--padding-start': '0px',
          '--padding-end': '0px',
          '--overflow': 'hidden',
        }}
      >
        {/* Toggle für Runs-Ansicht als Button unten rechts */}
        <div
          style={{
            position: 'absolute',
            bottom: '88px', // weiter oben, damit nicht hinter Bottom Bar
            left: '24px', // jetzt links statt rechts
            zIndex: 1002,
            background: 'var(--ion-color-secondary)', // Orange wie Legende
            borderRadius: '12px',
            padding: '10px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          <IonLabel style={{ color: 'white', fontSize: 16, fontWeight: 600 }}>
            {showOnlyOwnRuns ? (
              <Trans i18nKey="ONLY_PERSONAL_RUNS">Private</Trans>
            ) : (
              <Trans i18nKey="ALL_PUBLIC_RUNS">Public</Trans>
            )}
          </IonLabel>
          <IonToggle
            checked={showOnlyOwnRuns}
            onIonChange={(e) => setShowOnlyOwnRuns(e.detail.checked)}
            color="secondary"
            style={{ marginLeft: 8 }}
          />
        </div>
        {/* Map und Legende */}
        <div className="map-container">
          <MapComponent
            lat={mapCenter.lat}
            long={mapCenter.lng}
            markers={allMarkers}
            showCurrentLocation={true}
            defaultZoom={allMarkers.length > 0 ? 8 : 10}
            height={window.innerHeight}
          />
        </div>
        {/* Legende etwas weiter oben */}
        {/* <div
          style={{
            position: 'absolute',
            top: '40px', // weiter oben
            left: '16px',
            background: 'var(--ion-color-secondary)',
            padding: '12px',
            borderRadius: '12px',
            fontSize: '16px',
            zIndex: 1001,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              color: 'white',
              marginBottom: '8px',
              gap: '4px',
            }}
          >
            <Icon name="HotSpotMapIcon" size="small" color="white" /> Runs
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px',
              color: 'white',
              gap: '4px',
            }}
          >
            <Icon name="FriendsIcon" size="small" color="white" /> Events
          </div>
        </div> */}
      </IonContent>
    </IonPage>
  );
});

export default MapPage;
